<template>
  <div class="maintenance-form-wrapper">
    <!-- Top Navigation -->
    <div class="top-navigation">
      <div class="nav-left">
        <el-button text @click="goToMyReservations">
          <el-icon><Search /></el-icon>
          {{ t('navigation.myReservations') }}
        </el-button>
      </div>
      <div class="nav-right">
        <el-dropdown @command="handleLanguageChange">
          <span class="el-dropdown-link">
            {{ currentLanguageName }}
            <el-icon class="el-icon--right">
              <arrow-down />
            </el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="zh-TW">{{ t('language.zhTW') }}</el-dropdown-item>
              <el-dropdown-item command="zh-CN">{{ t('language.zhCN') }}</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>

    <div class="maintenance-form-container">
      <!-- Form Header -->
      <div class="form-header">
        <h1 class="form-title">{{ t('form.title') }}</h1>
        <p class="welcome-text">{{ t('form.welcome') }}</p>
      </div>

      <!-- Main Form -->
      <el-form
        ref="formRef"
        :model="formData"
        :rules="rules"
        label-position="top"
        class="maintenance-form"
      >
        <!-- Email -->
        <div class="form-section">
          <el-form-item prop="email">
            <template #label>
              <span class="field-label">{{ t('form.email') }}</span>
            </template>
            <el-input
              v-model="formData.email"
              :placeholder="t('form.emailPlaceholder')"
            />
          </el-form-item>
        </div>

        <!-- Name -->
        <div class="form-section">
          <el-form-item prop="name">
            <template #label>
              <span class="field-label">{{ t('form.name') }}</span>
            </template>
            <el-input
              v-model="formData.name"
              :placeholder="t('form.namePlaceholder')"
            />
          </el-form-item>
        </div>

        <!-- Phone -->
        <div class="form-section">
          <el-form-item prop="phone">
            <template #label>
              <span class="field-label">{{ t('form.phone') }}</span>
            </template>
            <el-input
              v-model="formData.phone"
              :placeholder="t('form.phonePlaceholder')"
            />
          </el-form-item>
        </div>

        <!-- Address -->
        <div class="form-section">
          <el-form-item prop="address">
            <template #label>
              <span class="field-label">{{ t('form.address') }}</span>
            </template>
            <el-input
              v-model="formData.address"
              :placeholder="t('form.addressPlaceholder')"
            />
          </el-form-item>
        </div>

        <!-- Line ID -->
        <div class="form-section line-id-section">
          <el-form-item prop="lineId">
            <template #label>
              <span class="field-label">{{ t('form.lineId') }}</span>
              <p class="field-hint">{{ t('form.lineIdHint') }}</p>
            </template>
            <div class="line-id-content">
              <div class="image-center">
                <img src="@/assets/1.png" alt="Line ID 設定" class="line-id-image" />
              </div>
              <el-input
                v-model="formData.lineId"
                :placeholder="t('form.lineIdPlaceholder')"
                class="line-id-input"
              />
            </div>
          </el-form-item>
        </div>

        <!-- Urgent Case Type -->
        <div class="form-section">
          <el-form-item prop="urgentType">
            <template #label>
              <span class="field-label">{{ t('form.isUrgent') }}</span>
            </template>
            <div class="pricing-info image-center">
              <img src="@/assets/2.png" alt="場勘報價服務" class="pricing-image" />
            </div>
            <el-radio-group v-model="formData.urgentType" class="radio-group-vertical">
              <el-radio label="general">{{ t('form.urgentGeneral') }}</el-radio>
              <el-radio label="urgent">{{ t('form.urgentEmergency') }}</el-radio>
            </el-radio-group>
          </el-form-item>
        </div>

        <!-- Repair Type -->
        <div class="form-section">
          <el-form-item prop="repairType">
            <template #label>
              <span class="field-label">{{ t('form.repairType') }}</span>
            </template>
            <!-- Tree-like structure: Categories with nested items -->
            <div class="repair-type-tree">
              <div 
                v-for="category in repairCategories" 
                :key="category.categoryId" 
                class="repair-category-node"
              >
                <!-- Category header -->
                <div 
                  class="category-header" 
                  :class="{ active: selectedRepairCategory === category.categoryName }"
                  @click="toggleCategory(category.categoryName)"
                >
                  <el-icon class="expand-icon">
                    <ArrowRight v-if="selectedRepairCategory !== category.categoryName" />
                    <ArrowDown v-else />
                  </el-icon>
                  <span class="category-name">{{ category.categoryName }}</span>
                </div>
                <!-- Category items (nested) -->
                <div 
                  v-if="selectedRepairCategory === category.categoryName" 
                  class="category-items"
                >
                  <div v-if="repairItemsLoading" class="repair-items-loading">
                    <el-icon class="is-loading"><Loading /></el-icon>
                  </div>
                  <template v-else-if="repairItems.length > 0">
                    <div v-for="item in repairItems" :key="item.templateId" class="repair-item-node">
                      <el-checkbox v-model="formData.repairTypes" :label="item.itemName" />
                      <span v-if="formData.repairTypes.includes(item.itemName)" class="repair-item-detail">
                        {{ item.price }}<template v-if="item.remark"> · {{ item.remark }}</template>
                      </span>
                    </div>
                  </template>
                  <div v-else class="no-repair-items">{{ t('form.noRepairItems') }}</div>
                </div>
              </div>
            </div>
          </el-form-item>
        </div>

        <!-- Description -->
        <div class="form-section">
          <el-form-item prop="description">
            <template #label>
              <span class="field-label">{{ t('form.description') }}</span>
            </template>
            <el-input
              v-model="formData.description"
              type="textarea"
              :rows="3"
              :placeholder="t('form.descriptionPlaceholder')"
            />
          </el-form-item>
        </div>

        <!-- Image Upload -->
        <div class="form-section">
          <el-form-item prop="images">
            <template #label>
              <span class="field-label">{{ t('form.images') }}</span>
            </template>
            <div class="upload-guide image-center">
              <img src="@/assets/3.png" alt="拍攝參考" class="guide-image" />
            </div>
            <el-upload
              v-model:file-list="fileList"
              action="#"
              list-type="picture-card"
              :auto-upload="false"
              :limit="9"
              :accept="acceptFileTypes"
              :on-preview="handlePictureCardPreview"
              :on-remove="handleRemove"
              :on-change="handleChange"
              :before-upload="beforeUpload"
              class="image-uploader"
            >
              <el-icon><Plus /></el-icon>
            </el-upload>
          </el-form-item>
        </div>

        <!-- How did you know -->
        <div class="form-section">
          <el-form-item prop="howKnow">
            <template #label>
              <span class="field-label">{{ t('form.howKnow') }}</span>
            </template>
            <el-radio-group v-model="formData.howKnow" class="radio-group-vertical">
              <el-radio label="fate">{{ t('form.howKnowOptions.fate') }}</el-radio>
              <el-radio label="referral">{{ t('form.howKnowOptions.referral') }}</el-radio>
              <el-radio label="line">{{ t('form.howKnowOptions.line') }}</el-radio>
              <el-radio label="fb">{{ t('form.howKnowOptions.fb') }}</el-radio>
              <el-radio label="ad">{{ t('form.howKnowOptions.ad') }}</el-radio>
              <el-radio label="other">{{ t('form.howKnowOptions.other') }}</el-radio>
            </el-radio-group>
            <el-input
              v-if="formData.howKnow === 'other'"
              v-model="formData.howKnowOther"
              :placeholder="t('form.otherPlaceholder')"
              style="margin-top: 12px;"
            />
          </el-form-item>
        </div>

        <!-- Referrer -->
        <div class="form-section">
          <el-form-item prop="referrer">
            <template #label>
              <span class="field-label">{{ t('form.referrer') }}</span>
            </template>
            <el-input
              v-model="formData.referrer"
              :placeholder="t('form.referrerPlaceholder')"
            />
          </el-form-item>
        </div>

        <!-- Join LINE Section -->
        <div class="form-section">
          <el-form-item prop="joinedLine">
            <template #label>
              <span class="field-label">{{ t('form.joinedLine') }}</span>
              <p class="field-hint">{{ t('form.joinedLineHint') }}<br>
              {{ t('form.lineAccount') }} <a href="https://lin.ee/4093KX5" target="_blank" class="line-link">https://lin.ee/4093KX5</a></p>
            </template>
            <div class="image-center">
              <div class="qr-code-container">
                <img src="@/assets/4.png" alt="LINE QR Code" class="qr-code-image" />
              </div>
              <el-radio-group v-model="formData.joinedLine" class="radio-group-vertical" style="margin-top: 16px;">
                <el-radio label="yes">{{ t('form.yes') }}</el-radio>
                <el-radio label="no">{{ t('form.no') }}</el-radio>
              </el-radio-group>
            </div>
          </el-form-item>
        </div>

        <!-- Form Actions -->
        <div class="form-actions">
          <el-button type="primary" @click="submitForm">
            {{ t('form.submit') }}
          </el-button>
          <el-button @click="resetForm">
            {{ t('form.clear') }}
          </el-button>
        </div>

      </el-form>
    </div>

    <!-- Preview Dialog for Images and Videos -->
    <el-dialog v-model="dialogVisible" :title="isVideoPreview ? t('form.videoPreview') : t('form.imagePreview')" width="80%" destroy-on-close>
      <div class="preview-container">
        <video v-if="isVideoPreview" :src="dialogMediaUrl" controls class="preview-video"></video>
        <img v-else :src="dialogMediaUrl" alt="Preview" class="preview-image" />
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage, FormInstance, FormRules, UploadUserFile, UploadProps } from 'element-plus'
import { Plus, ArrowDown, ArrowRight, QuestionFilled, Search, Loading } from '@element-plus/icons-vue'
import axios from 'axios'

const { t, locale } = useI18n()

// Repair type categories and items
interface RepairCategory {
  categoryId: number
  categoryName: string
  sortOrder: number
  status: number
}

interface RepairItem {
  templateId: number
  repairType: string
  itemName: string
  price: string
  remark: string
}

const repairCategories = ref<RepairCategory[]>([])
const repairItems = ref<RepairItem[]>([])
const selectedRepairCategory = ref('')
const repairItemsLoading = ref(false)

// Fetch repair categories on mount
onMounted(async () => {
  await fetchRepairCategories()
})

// Fetch all repair categories
const fetchRepairCategories = async () => {
  try {
    const langMap: Record<string, string> = {
      'zh-TW': 'zh_TW',
      'zh-CN': 'zh_CN',
      'en': 'en_US'
    }
    const apiLang = langMap[locale.value] || 'zh_TW'
    
    const response = await axios.get(`/api/repair-price/types?lang=${apiLang}`)
    if (response.data.code === 200) {
      repairCategories.value = response.data.data || []
    }
  } catch (error) {
    console.error('Failed to fetch repair categories:', error)
  }
}

// Fetch repair items by category
const fetchRepairItems = async (repairType: string) => {
  repairItemsLoading.value = true
  try {
    const langMap: Record<string, string> = {
      'zh-TW': 'zh_TW',
      'zh-CN': 'zh_CN',
      'en': 'en_US'
    }
    const apiLang = langMap[locale.value] || 'zh_TW'
    
    const response = await axios.get(`/api/repair-price/by-type?lang=${apiLang}&repairType=${encodeURIComponent(repairType)}`)
    if (response.data.code === 200) {
      repairItems.value = response.data.data || []
    }
  } catch (error) {
    console.error('Failed to fetch repair items:', error)
    repairItems.value = []
  } finally {
    repairItemsLoading.value = false
  }
}

// Handle repair category change
const handleRepairCategoryChange = (categoryName: string) => {
  // Clear previously selected items when category changes
  formData.repairTypes = []
  if (categoryName) {
    fetchRepairItems(categoryName)
  } else {
    repairItems.value = []
  }
}

// Toggle category expansion
const toggleCategory = (categoryName: string) => {
  if (selectedRepairCategory.value === categoryName) {
    // Collapse if already selected
    selectedRepairCategory.value = ''
    repairItems.value = []
  } else {
    // Expand and load items
    selectedRepairCategory.value = categoryName
    formData.repairTypes = []
    fetchRepairItems(categoryName)
  }
}

// Language switcher
const currentLanguageName = computed(() => {
  return t(`language.${locale.value.replace('-', '')}`)
})

const handleLanguageChange = (lang: string) => {
  locale.value = lang
}

// Form reference
const formRef = ref<FormInstance>()

// Form data
const formData = reactive({
  email: '',
  name: '',
  phone: '',
  address: '',
  lineId: '',
  urgentType: '',
  repairTypes: [] as string[],
  description: '',
  images: [] as string[],
  howKnow: '',
  howKnowOther: '',
  referrer: '',
  joinedLine: ''
})

// File list for upload
const fileList = ref<UploadUserFile[]>([])

// Image IDs from upload
const uploadedImageIds = ref<number[]>([])

// Media preview (images and videos)
const dialogVisible = ref(false)
const dialogMediaUrl = ref('')
const isVideoPreview = ref(false)

// Store original video URLs for preview (uid -> url)
const videoOriginalUrls = ref<Record<string, string>>({})

// Accepted file types (images and videos)
const acceptFileTypes = 'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/x-msvideo,video/webm'

// Validation rules
const rules = reactive<FormRules>({
  email: [
    { required: true, message: () => t('form.required'), trigger: 'blur' },
    { type: 'email', message: () => t('form.invalidEmail'), trigger: 'blur' }
  ],
  name: [
    { required: true, message: () => t('form.required'), trigger: 'blur' }
  ],
  phone: [
    { required: true, message: () => t('form.required'), trigger: 'blur' },
    { 
      validator: (rule: any, value: string, callback: any) => {
        if (value && !/^\d{10}$/.test(value)) {
          callback(new Error(t('form.invalidPhone')))
        } else {
          callback()
        }
      }, 
      trigger: 'blur' 
    }
  ],
  address: [
    { required: true, message: () => t('form.required'), trigger: 'blur' }
  ],
  lineId: [],
  urgentType: [
    { required: true, message: () => t('form.required'), trigger: 'change' }
  ]
})

// Upload handlers
const handlePictureCardPreview: UploadProps['onPreview'] = (uploadFile) => {
  const fileType = uploadFile.raw?.type || ''
  const isVideo = fileType.startsWith('video/')
  isVideoPreview.value = isVideo
  // Use original video URL for preview, thumbnail URL for images
  if (isVideo && uploadFile.uid && videoOriginalUrls.value[uploadFile.uid]) {
    dialogMediaUrl.value = videoOriginalUrls.value[uploadFile.uid]
  } else {
    dialogMediaUrl.value = uploadFile.url!
  }
  dialogVisible.value = true
}

// Before upload validation
const beforeUpload: UploadProps['beforeUpload'] = (rawFile) => {
  const isImage = rawFile.type.startsWith('image/')
  const isVideo = rawFile.type.startsWith('video/')
  if (!isImage && !isVideo) {
    ElMessage.error(t('form.invalidFileType'))
    return false
  }
  // Limit file size: images 10MB, videos 100MB
  const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024
  if (rawFile.size > maxSize) {
    ElMessage.error(isVideo ? t('form.videoTooLarge') : t('form.imageTooLarge'))
    return false
  }
  return true
}

const handleRemove: UploadProps['onRemove'] = (uploadFile, uploadFiles) => {
  // Remove corresponding image ID if exists
  const index = fileList.value.findIndex(f => f.uid === uploadFile.uid)
  if (index !== -1 && uploadedImageIds.value[index]) {
    uploadedImageIds.value.splice(index, 1)
  }
  // Clean up video URL if exists
  if (uploadFile.uid && videoOriginalUrls.value[uploadFile.uid]) {
    URL.revokeObjectURL(videoOriginalUrls.value[uploadFile.uid])
    delete videoOriginalUrls.value[uploadFile.uid]
  }
}

// Generate video thumbnail from first frame
const generateVideoThumbnail = (file: File): Promise<string> => {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.muted = true
    video.playsInline = true
    
    video.onloadeddata = () => {
      video.currentTime = 0.1 // Seek to first frame
    }
    
    video.onseeked = () => {
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      ctx?.drawImage(video, 0, 0, canvas.width, canvas.height)
      const thumbnail = canvas.toDataURL('image/jpeg', 0.8)
      URL.revokeObjectURL(video.src)
      resolve(thumbnail)
    }
    
    video.onerror = () => {
      URL.revokeObjectURL(video.src)
      resolve('') // Return empty on error
    }
    
    video.src = URL.createObjectURL(file)
  })
}

const handleChange: UploadProps['onChange'] = async (uploadFile, uploadFiles) => {
  // Upload file when added
  if (uploadFile.status === 'ready' && uploadFile.raw) {
    // Generate thumbnail for video files and store original URL
    const isVideo = uploadFile.raw.type.startsWith('video/')
    if (isVideo) {
      // Store original video URL for preview
      const originalUrl = URL.createObjectURL(uploadFile.raw)
      videoOriginalUrls.value[uploadFile.uid] = originalUrl
      
      // Generate thumbnail for display
      const thumbnail = await generateVideoThumbnail(uploadFile.raw)
      if (thumbnail) {
        uploadFile.url = thumbnail
      }
    }
    try {
      const formData = new FormData()
      formData.append('file', uploadFile.raw)
      formData.append('businessType', 'RESERVATION')
      formData.append('businessId', '0')
      formData.append('uploadUserId', '0')
      
      // Get current language for API
      const langMap: Record<string, string> = {
        'zh-TW': 'zh_TW',
        'zh-CN': 'zh_CN',
        'en': 'en_US'
      }
      const apiLang = langMap[locale.value] || 'zh_TW'
      
      const response = await axios.post(
        `/api/image/upload?lang=${apiLang}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Accept-Language': apiLang
          }
        }
      )
      
      if (response.data.code === 200) {
        // Store image/video ID (backend may return imageId, mediaId, or videoId)
        const uploadedId = response.data.data.imageId || response.data.data.mediaId || response.data.data.videoId
        if (uploadedId) {
          uploadedImageIds.value.push(uploadedId)
        }
        ElMessage.success(t('form.uploadSuccess'))
      } else {
        ElMessage.error(response.data.message || t('form.uploadFailed'))
        // Remove failed upload from file list
        const index = fileList.value.findIndex(f => f.uid === uploadFile.uid)
        if (index !== -1) {
          fileList.value.splice(index, 1)
        }
      }
    } catch (error: any) {
      console.error('Upload error:', error)
      const errorMsg = error.response?.data?.message || t('form.uploadFailed')
      ElMessage.error(errorMsg)
      // Remove failed upload from file list
      const index = fileList.value.findIndex(f => f.uid === uploadFile.uid)
      if (index !== -1) {
        fileList.value.splice(index, 1)
      }
    }
  }
}

// Submit form
const submitForm = async () => {
  if (!formRef.value) return

  try {
    const valid = await formRef.value.validate()
    if (valid) {
      // Repair types are now item names from API, join them directly
      const repairTypesText = formData.repairTypes.join(',')

      // Convert source channel code to Chinese text
      const sourceChannelMap: Record<string, string> = {
        'fate': t('form.howKnowOptions.fate'),
        'referral': t('form.howKnowOptions.referral'),
        'line': t('form.howKnowOptions.line'),
        'fb': t('form.howKnowOptions.fb'),
        'ad': t('form.howKnowOptions.ad'),
        'other': t('form.howKnowOptions.other')
      }
      const sourceChannelText = sourceChannelMap[formData.howKnow] || formData.howKnow

      // Map form data to API format
      const submitData = {
        realName: formData.name,
        phone: formData.phone,
        email: formData.email,
        lineId: formData.lineId,
        address: formData.address,
        isUrgent: formData.urgentType === 'urgent' ? 1 : 0,
        repairType: repairTypesText, // 中文修缮类型，逗号分隔
        supplementDesc: formData.description,
        sourceChannel: sourceChannelText, // 中文来源渠道
        recommenderName: formData.referrer,
        isJoin: formData.joinedLine === 'yes' ? 1 : 0,
        imageIds: uploadedImageIds.value.filter(id => id != null), // 上传的图片/视频ID列表，过滤null值
        userId: 0 // 未登录用户
      }

      // Get current language for API
      const langMap: Record<string, string> = {
        'zh-TW': 'zh_TW',
        'zh-CN': 'zh_CN',
        'en': 'en_US'
      }
      const apiLang = langMap[locale.value] || 'zh_TW'

      // Submit to API with language parameter
      const response = await axios.post(
        `/api/reservation/create?lang=${apiLang}`,
        submitData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': apiLang
          }
        }
      )
      
      if (response.data.code === 200) {
        // Success - redirect to success page with reservation data
        const reservationData = {
          reservationId: response.data.data.reservationId,
          phone: response.data.data.phone,
          createTime: response.data.data.createTime
        }
        const dataStr = encodeURIComponent(JSON.stringify(reservationData))
        window.location.href = `/#/success/${dataStr}`
      } else {
        ElMessage.error(response.data.message || t('form.submitFailed'))
      }
    }
  } catch (error: any) {
    console.error('Submit error:', error)
    const errorMsg = error.response?.data?.message || '提交失敗，請稍後再試'
    ElMessage.error(errorMsg)
  }
}

// Reset form
const resetForm = () => {
  formRef.value?.resetFields()
  fileList.value = []
  uploadedImageIds.value = []
}

// Navigate to my reservations
const goToMyReservations = () => {
  window.location.href = '/#/my-reservations'
}
</script>

<style scoped>
.maintenance-form-wrapper {
  min-height: 100vh;
  background: #f0ebf8;
  padding: 20px 0;
}

.top-navigation {
  max-width: 760px;
  margin: 0 auto 20px;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.nav-left .el-button {
  color: #673ab7;
  font-weight: 500;
  font-size: 15px;
}

.nav-left .el-button:hover {
  color: #5e35b1;
}

.nav-left .el-icon {
  margin-right: 4px;
}

.nav-right {
  display: flex;
  align-items: center;
}

.maintenance-form-container {
  max-width: 760px;
  margin: 0 auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.el-dropdown-link {
  cursor: pointer;
  color: #673ab7;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 14px;
  font-weight: 500;
}

.form-header {
  background: #673ab7;
  padding: 32px 24px;
  border-top: 10px solid #673ab7;
  text-align: left;
}

.form-title {
  font-size: 32px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: #ffffff;
  line-height: 1.3;
}

.welcome-text {
  font-size: 14px;
  color: #ffffff;
  line-height: 1.8;
  margin: 0;
  opacity: 0.95;
}

.logo-section {
  background: #fff;
  padding: 24px;
  border-bottom: 1px solid #dadce0;
}

.logo-image {
  max-width: 100%;
  width: 100%;
  height: auto;
  border-radius: 8px;
  object-fit: contain;
}

.maintenance-form {
  padding: 0;
}

.form-section {
  padding: 24px;
  border-bottom: 1px solid #dadce0;
}

.field-label {
  font-size: 14px;
  color: #202124;
  font-weight: 400;
  display: block;
  margin-bottom: 8px;
}

.required {
  color: #d93025;
  margin-left: 4px;
}

.field-hint {
  font-size: 12px;
  color: #5f6368;
  margin: 4px 0 12px 0;
}

.email-checkbox {
  font-size: 13px;
}

.maintenance-form :deep(.el-form-item) {
  margin-bottom: 0;
}

.maintenance-form :deep(.el-form-item__label) {
  padding: 0;
  line-height: 1.5;
}

.maintenance-form :deep(.el-input__wrapper) {
  border-radius: 4px;
  border: 1px solid #dadce0;
  box-shadow: none;
  padding: 8px 12px;
}

.maintenance-form :deep(.el-input__wrapper:hover) {
  border-color: #202124;
}

.maintenance-form :deep(.el-input__wrapper.is-focus) {
  border-color: #673ab7;
  box-shadow: none;
}

.maintenance-form :deep(.el-input__inner) {
  font-size: 14px;
}

.line-id-section {
  display: block;
}

.line-id-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.image-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
}

.image-center img {
  display: block;
  margin: 0 auto;
}

.line-id-image {
  max-width: 700px;
  width: 90%;
  height: auto;
  border: 1px solid #dadce0;
  border-radius: 4px;
  display: block;
  margin: 0 auto;
  object-fit: contain;
}

.radio-group-vertical,
.checkbox-group-vertical {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.radio-group-vertical :deep(.el-radio),
.checkbox-group-vertical :deep(.el-checkbox) {
  margin-right: 0;
}

.upload-guide {
  margin-bottom: 16px;
}

.guide-image {
  max-width: 600px;
  width: auto;
  height: auto;
  border: 1px solid #dadce0;
  border-radius: 4px;
  display: block;
  margin: 0 auto;
  object-fit: contain;
}

.image-uploader {
  width: 100%;
}

.pricing-info {
  margin: 16px 0;
}

.repair-type-tree {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.repair-category-node {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
}

.category-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  background: #fafafa;
  transition: background 0.2s;
}

.category-header:hover {
  background: #f0ebf8;
}

.category-header.active {
  background: #f0ebf8;
  border-bottom: 1px solid #e4e7ed;
}

.expand-icon {
  margin-right: 8px;
  color: #909399;
  font-size: 12px;
}

.category-name {
  font-size: 14px;
  color: #303133;
}

.category-items {
  padding: 8px 16px 8px 36px;
  background: #fff;
}

.repair-item-node {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 6px 0;
}

.repair-item-node :deep(.el-checkbox) {
  margin-right: 0;
}

.repair-item-detail {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}

.no-repair-items {
  color: #909399;
  font-size: 13px;
  padding: 8px 0;
}

.repair-items-loading {
  display: flex;
  justify-content: center;
  padding: 12px 0;
  color: #673ab7;
}

.pricing-image {
  max-width: 100%;
  width: auto;
  height: auto;
  border-radius: 4px;
  display: block;
  margin: 0 auto;
  object-fit: contain;
}

.qr-code-container {
  display: flex;
  justify-content: center;
  margin: 0 0 16px 0;
}

.qr-code-image {
  width: 400px;
  height: 400px;
  border: 2px solid #202124;
  border-radius: 8px;
  padding: 8px;
  background: #fff;
  display: block;
  margin: 0 auto;
}

.line-link {
  color: #673ab7;
  text-decoration: none;
}

.line-link:hover {
  text-decoration: underline;
}

.form-actions {
  padding: 24px;
  display: flex;
  justify-content: center;
  gap: 12px;
}

.form-actions .el-button {
  font-size: 14px;
  padding: 8px 24px;
  border-radius: 4px;
}

.form-actions .el-button--primary {
  background: #673ab7;
  border-color: #673ab7;
}

.form-actions .el-button--primary:hover {
  background: #5e35b1;
  border-color: #5e35b1;
}

/* Responsive design */
@media (max-width: 768px) {
  .maintenance-form-wrapper {
    padding: 10px;
  }

  .maintenance-form-container {
    max-width: 100%;
    margin: 0 10px;
  }

  .language-switcher {
    max-width: 100%;
    padding: 0 10px;
  }

  .form-header {
    padding: 20px 16px;
  }

  .form-title {
    font-size: 24px;
  }

  .welcome-text {
    font-size: 13px;
  }

  .logo-section {
    padding: 16px;
  }

  .form-section {
    padding: 16px;
  }

  .field-label {
    font-size: 13px;
  }

  .field-hint {
    font-size: 11px;
  }

  .line-id-image {
    max-width: 100%;
    width: 95%;
  }

  .pricing-image {
    max-width: 100%;
    width: 100%;
  }

  .guide-image {
    max-width: 100%;
    width: 100%;
  }

  .radio-group-horizontal {
    gap: 8px;
  }

  .qr-code-image {
    width: 250px;
    height: 250px;
  }

  .form-actions {
    padding: 16px;
    flex-direction: column;
  }

  .form-actions .el-button {
    width: 100%;
  }

  .radio-group-vertical,
  .checkbox-group-vertical {
    gap: 10px;
  }

  .maintenance-form :deep(.el-input__wrapper) {
    padding: 6px 10px;
  }

  .maintenance-form :deep(.el-radio__label),
  .maintenance-form :deep(.el-checkbox__label) {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .maintenance-form-wrapper {
    padding: 5px;
  }

  .maintenance-form-container {
    margin: 0 5px;
    border-radius: 4px;
  }

  .form-header {
    padding: 16px 12px;
  }

  .form-title {
    font-size: 20px;
  }

  .welcome-text {
    font-size: 12px;
    line-height: 1.6;
  }

  .logo-section {
    padding: 12px;
  }

  .form-section {
    padding: 12px;
  }

  .field-label {
    font-size: 12px;
  }

  .field-hint {
    font-size: 10px;
  }

  .line-id-image {
    width: 100%;
  }

  .qr-code-image {
    width: 200px;
    height: 200px;
  }

  .form-actions {
    padding: 12px;
  }

  .form-actions .el-button {
    font-size: 13px;
    padding: 10px 20px;
  }

  .maintenance-form :deep(.el-input__inner) {
    font-size: 13px;
  }

  .maintenance-form :deep(.el-textarea__inner) {
    font-size: 13px;
  }

  .maintenance-form :deep(.el-radio__label),
  .maintenance-form :deep(.el-checkbox__label) {
    font-size: 12px;
  }

  .maintenance-form :deep(.el-upload--picture-card) {
    width: 80px;
    height: 80px;
  }

  .maintenance-form :deep(.el-upload-list--picture-card .el-upload-list__item) {
    width: 80px;
    height: 80px;
  }
}

/* Preview dialog */
.preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.preview-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

.preview-video {
  max-width: 100%;
  max-height: 70vh;
}

/* Extra small devices */
@media (max-width: 360px) {
  .form-title {
    font-size: 18px;
  }

  .welcome-text {
    font-size: 11px;
  }

  .qr-code-image {
    width: 180px;
    height: 180px;
  }
}
</style>
